import React, { useState, useMemo, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Icon, Input, Segment } from 'semantic-ui-react';

var DropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

var DropdownTrigger = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.67857143em 1em;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 0.28571429rem;
  background: #fff;
  cursor: pointer;
  min-height: 38px;

  &:hover {
    border-color: rgba(34, 36, 38, 0.35);
  }

  ${function(props) { return props.isOpen ? 'border-color: #85b7d9; border-bottom-left-radius: 0; border-bottom-right-radius: 0;' : ''; }}
  ${function(props) { return props.disabled ? 'opacity: 0.45; cursor: default; pointer-events: none;' : ''; }}
`;

var DropdownText = styled.span`
  color: ${function(props) { return props.isplaceholder ? 'rgba(191,191,191,.87)' : 'rgba(0,0,0,.87)'; }};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

var DropdownMenu = styled.div`
  position: absolute;
  z-index: 1000;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-top: none;
  border-bottom-left-radius: 0.28571429rem;
  border-bottom-right-radius: 0.28571429rem;
  box-shadow: 0 2px 3px 0 rgba(34, 36, 38, 0.15);
`;

var SearchContainer = styled.div`
  padding: 0.5em;
  border-bottom: 1px solid rgba(34, 36, 38, 0.1);
  background: #fff;
`;

var TreeContainer = styled.div`
  padding: 0.25em 0;
`;

var TreeNode = styled.div`
  padding-left: ${function(props) { return (props.level * 20) + 'px'; }};
`;

var TreeNodeContent = styled.div`
  display: flex;
  align-items: center;
  padding: 0.25em 0.5em;
  cursor: pointer;
  transition: background 0.1s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }

  ${function(props) { return props.selected ? 'background: rgba(33, 133, 208, 0.15); font-weight: 600;' : ''; }}
  ${function(props) { return props.haschildren ? 'font-weight: 600; color: rgba(0, 0, 0, 0.85);' : ''; }}
  ${function(props) { return props.disabled ? 'opacity: 0.5; cursor: not-allowed;' : ''; }}
`;

var ExpandIcon = styled.span`
  width: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 2px;
  cursor: pointer;

  &:hover {
    color: #2185d0;
  }
`;

var NodeLabel = styled.span`
  flex: 1;
`;

var NoResults = styled.div`
  padding: 1em;
  text-align: center;
  color: rgba(0, 0, 0, 0.4);
`;

function buildTree(items, parentId) {
  var result = [];
  for (var i = 0; i < items.length; i++) {
    var item = items[i];
    var itemParentId = item.rdS_Unit_ParentID !== undefined ? item.rdS_Unit_ParentID : item.RDS_Unit_ParentID;

    var isRootLevel = parentId === null && (itemParentId === null || itemParentId === undefined || itemParentId === 0);
    var isChildOfParent = parentId !== null && itemParentId === parentId;

    if (isRootLevel || isChildOfParent) {
      var itemId = item.rdS_Unit_ID !== undefined ? item.rdS_Unit_ID : item.RDS_Unit_ID;
      var children = buildTree(items, itemId);
      result.push({
        id: itemId,
        name: item.rdS_Unit_Name !== undefined ? item.rdS_Unit_Name : item.RDS_Unit_Name,
        parentId: itemParentId,
        isUnitIDExist: item.isUnitIDExist,
        children: children
      });
    }
  }
  return result;
}

function filterTree(nodes, searchTerm, matchedIds) {
  if (!searchTerm) return nodes;

  var result = [];
  for (var i = 0; i < nodes.length; i++) {
    var node = nodes[i];
    var nameMatches = node.name.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1;
    var filteredChildren = filterTree(node.children, searchTerm, matchedIds);

    if (nameMatches || filteredChildren.length > 0) {
      if (nameMatches) {
        matchedIds.push(node.id);
      }
      result.push(Object.assign({}, node, { children: filteredChildren }));
    }
  }
  return result;
}

function getAllParentIds(items, nodeId) {
  var parentIds = [];
  var currentId = nodeId;

  while (currentId) {
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var itemId = item.rdS_Unit_ID || item.RDS_Unit_ID;
      if (itemId === currentId) {
        var parentId = item.rdS_Unit_ParentID || item.RDS_Unit_ParentID;
        if (parentId) {
          parentIds.push(parentId);
          currentId = parentId;
        } else {
          currentId = null;
        }
        break;
      }
    }
    if (i === items.length) {
      currentId = null;
    }
  }
  return parentIds;
}

function TreeNodeComponent(props) {
  var node = props.node;
  var level = props.level;
  var expandedNodes = props.expandedNodes;
  var toggleExpand = props.toggleExpand;
  var selectedId = props.selectedId;
  var onSelect = props.onSelect;
  var searchTerm = props.searchTerm;

  var hasChildren = node.children && node.children.length > 0;
  var isExpanded = expandedNodes[node.id];
  var isSelected = selectedId === node.id;

  var handleToggleExpand = function(e) {
    e.stopPropagation();
    toggleExpand(node.id);
  };

  var handleSelect = function() {
    onSelect(node);
  };

  return React.createElement('div', null,
    React.createElement(TreeNode, { level: level },
      React.createElement(TreeNodeContent, { selected: isSelected, haschildren: hasChildren ? 'true' : undefined, onClick: handleSelect },
        React.createElement(ExpandIcon, { onClick: hasChildren ? handleToggleExpand : undefined },
          hasChildren
            ? React.createElement(Icon, { name: isExpanded ? 'caret down' : 'caret right', size: 'small' })
            : React.createElement('span', { style: { width: '14px' } })
        ),
        React.createElement(NodeLabel, null, node.name)
      )
    ),
    hasChildren && isExpanded && node.children.map(function(child) {
      return React.createElement(TreeNodeComponent, {
        key: child.id,
        node: child,
        level: level + 1,
        expandedNodes: expandedNodes,
        toggleExpand: toggleExpand,
        selectedId: selectedId,
        onSelect: onSelect,
        searchTerm: searchTerm
      });
    })
  );
}

function TreeDropdown(props) {
  var items = props.items || [];
  var value = props.value;
  var onChange = props.onChange;
  var placeholder = props.placeholder || 'Select...';
  var loading = props.loading;
  var disabled = props.disabled;

  var containerRef = useRef(null);
  var menuRef = useRef(null);

  var initialExpanded = {};
  var initialExpandedState = useState(initialExpanded);
  var expandedNodes = initialExpandedState[0];
  var setExpandedNodes = initialExpandedState[1];

  var openState = useState(false);
  var isOpen = openState[0];
  var setIsOpen = openState[1];

  var searchState = useState('');
  var searchTerm = searchState[0];
  var setSearchTerm = searchState[1];

  var tree = useMemo(function() {
    return buildTree(items, null);
  }, [items]);

  var matchedIds = [];
  var filteredTree = useMemo(function() {
    matchedIds = [];
    return filterTree(tree, searchTerm, matchedIds);
  }, [tree, searchTerm]);

  useEffect(function() {
    if (searchTerm && matchedIds.length > 0) {
      var newExpanded = Object.assign({}, expandedNodes);
      for (var i = 0; i < matchedIds.length; i++) {
        var parentIds = getAllParentIds(items, matchedIds[i]);
        for (var j = 0; j < parentIds.length; j++) {
          newExpanded[parentIds[j]] = true;
        }
      }
      setExpandedNodes(newExpanded);
    }
  }, [searchTerm]);

  useEffect(function() {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return function() {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  var selectedName = useMemo(function() {
    if (!value) return null;
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var itemId = item.rdS_Unit_ID || item.RDS_Unit_ID;
      if (itemId === value) {
        return item.rdS_Unit_Name || item.RDS_Unit_Name;
      }
    }
    return null;
  }, [value, items]);

  var toggleExpand = function(nodeId) {
    setExpandedNodes(function(prev) {
      var newState = Object.assign({}, prev);
      newState[nodeId] = !prev[nodeId];
      return newState;
    });
  };

  var handleSelect = function(node) {
    onChange(node.id, node.name);
    setIsOpen(false);
    setSearchTerm('');
  };

  var handleTriggerClick = function() {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm('');
    }
  };

  var handleSearchChange = function(e) {
    setSearchTerm(e.target.value);
  };

  if (loading) {
    return React.createElement(DropdownContainer, null,
      React.createElement(DropdownTrigger, null,
        React.createElement(DropdownText, { isplaceholder: 'true' }, 'Loading...'),
        React.createElement(Icon, { name: 'spinner', loading: true })
      )
    );
  }

  return React.createElement(DropdownContainer, { ref: containerRef },
    React.createElement(DropdownTrigger, { isOpen: isOpen, disabled: disabled, onClick: handleTriggerClick },
      React.createElement(DropdownText, { isplaceholder: !selectedName ? 'true' : undefined }, selectedName || placeholder),
      React.createElement(Icon, { name: isOpen ? 'caret up' : 'caret down' })
    ),
    isOpen && React.createElement(DropdownMenu, { ref: menuRef },
      React.createElement(SearchContainer, null,
        React.createElement(Input, {
          fluid: true,
          icon: 'search',
          placeholder: 'Search units...',
          value: searchTerm,
          onChange: handleSearchChange,
          onClick: function(e) { e.stopPropagation(); }
        })
      ),
      React.createElement(TreeContainer, null,
        filteredTree.length > 0
          ? filteredTree.map(function(node) {
              return React.createElement(TreeNodeComponent, {
                key: node.id,
                node: node,
                level: 0,
                expandedNodes: expandedNodes,
                toggleExpand: toggleExpand,
                selectedId: value,
                onSelect: handleSelect,
                searchTerm: searchTerm
              });
            })
          : React.createElement(NoResults, null, 'No units found')
      )
    )
  );
}

export default TreeDropdown;
